import { z } from 'zod';

export enum GraderProvider {
  SPEECHSUPER = 'SPEECHSUPER',
}

export enum SpeechSuperRequestType {
  CONNECT = 'CONNECT',
  START = 'START',
}

/**
 * Interface for cached parameters with expiration
 */
export interface CachedParams {
  value: SpeechSuperConnectAppParams;
  expiresAt: number;
}

/**
 * Interface for pending request with expiration
 */
export interface PendingRequest {
  promise: Promise<CachedParams>;
  expiresAt: number;
}

export const SpeechSuperConnectAppParamsSchema = z.object({
  applicationId: z.string(),
  timestamp: z.string(),
  sig: z.string(),
});

export const SpeechSuperStartAppParamsSchema = SpeechSuperConnectAppParamsSchema.extend({
  userId: z.string(),
});

export const SpeechSuperGraderInitializationRequestSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(SpeechSuperRequestType.CONNECT),
  }),
  z.object({
    type: z.literal(SpeechSuperRequestType.START),
  }),
]);

export const InitializeSpeechSuperGraderMetadataSchema = z.object({
  requests: z.array(SpeechSuperGraderInitializationRequestSchema),
});

export const InitializePronunciationGraderInputSchema = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal(GraderProvider.SPEECHSUPER),
    metadata: InitializeSpeechSuperGraderMetadataSchema,
  }),
]);

export const SpeechSuperInitializationSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(SpeechSuperRequestType.CONNECT),
    ...SpeechSuperConnectAppParamsSchema.shape,
  }),
  z.object({
    type: z.literal(SpeechSuperRequestType.START),
    ...SpeechSuperStartAppParamsSchema.shape,
  }),
]);

export const SpeechSuperInitializationResponseMetadataSchema = z.object({
  responses: z.array(SpeechSuperInitializationSchema),
});

export const InitializePronunciationGraderResponseSchema = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal(GraderProvider.SPEECHSUPER),
    metadata: SpeechSuperInitializationResponseMetadataSchema,
  }),
]);

// Type Inference
export type SpeechSuperConnectAppParams = z.infer<typeof SpeechSuperConnectAppParamsSchema>;
export type SpeechSuperStartAppParams = z.infer<typeof SpeechSuperStartAppParamsSchema>;
export type SpeechSuperGraderInitializationRequest = z.infer<typeof SpeechSuperGraderInitializationRequestSchema>;
export type InitializeSpeechSuperGraderMetadata = z.infer<typeof InitializeSpeechSuperGraderMetadataSchema>;
export type InitializePronunciationGraderInput = z.infer<typeof InitializePronunciationGraderInputSchema>;
export type InitializePronunciationGraderPreparedInput = Omit<InitializePronunciationGraderInput, 'metadata'> & {
  metadata: string;
};
export type SpeechSuperInitialization = z.infer<typeof SpeechSuperInitializationSchema>;
export type SpeechSuperInitializationResponseMetadata = z.infer<typeof SpeechSuperInitializationResponseMetadataSchema>;
export type InitializePronunciationGraderResponse = z.infer<typeof InitializePronunciationGraderResponseSchema>;
